import { useMemo, useState } from "react";
import type { ChangeEvent } from "react";

import { useMutation } from "@tanstack/react-query";

import { asMutation } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { Button } from "simplydo/react/ui/button";
import { Checkbox } from "simplydo/react/ui/checkbox";
import { DateInput } from "simplydo/react/ui/date";
import { Divider } from "simplydo/react/ui/divider";
import { Dropdown, type DropdownProps } from "simplydo/react/ui/dropdown";
import { FormField, FormLabel } from "simplydo/react/ui/form";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H5 } from "simplydo/react/ui/header";
import { Input } from "simplydo/react/ui/input";
import { Message } from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import AiWarning from "components/lib/AiWarning";

type PlanChannel = { channel: string; frequency: string; count: number };

type PlanConfiguration = {
  from: string;
  to: string;
  name: string;
  channels: PlanChannel[];
  tone: "smartCasual" | "formal" | "auto";
};

type GeneratePlanModalProps = {
  onClose: () => void;
  challengeId: string;
  calendarId: string;
  generatePlanModalOpen: boolean;
  calendar: OpenAPI.GET<"/calendars/{calendar_id}/calendar">["response"]["calendar"];
};

const getDefaultDates = (
  cal: NonNullable<
    OpenAPI.GET<"/calendars/{calendar_id}/calendar">["response"]["calendar"]
  >,
) => {
  const datedEvents = (cal.events || []).filter(
    (event) => event.from && !event.isGenerated,
  );
  if (datedEvents.length > 0) {
    const firstEvent = datedEvents[0];
    const from = datedEvents.reduce(
      (min, event) => {
        const eventFrom = new Date(event.from!).toISOString();
        return eventFrom < min ? eventFrom : min;
      },
      new Date(firstEvent?.from ?? Date.now()).toISOString(),
    );
    const to = datedEvents.reduce(
      (max, event) => {
        const eventEnd = new Date(event.to || event.from!).toISOString();
        return eventEnd > max ? eventEnd : max;
      },
      new Date(firstEvent?.to || firstEvent?.from || Date.now()).toISOString(),
    );
    return { from, to };
  }

  const fromDate = new Date();
  const toDate = new Date(fromDate);
  toDate.setMonth(toDate.getMonth() + 1);
  return {
    from: fromDate.toISOString().split("T")[0] ?? "",
    to: toDate.toISOString().split("T")[0] ?? "",
  };
};

const GeneratePlanModal = ({
  onClose,
  challengeId,
  calendarId,
  generatePlanModalOpen,
  calendar,
}: GeneratePlanModalProps) => {
  const user = useAppSelector((state) => state.user);

  const defaultDates = useMemo(
    () => (calendar ? getDefaultDates(calendar) : { from: "", to: "" }),
    [calendar],
  );

  const [planConfiguration, setPlanConfiguration] = useState<PlanConfiguration>(
    {
      from: defaultDates.from,
      to: defaultDates.to,
      channels: [],
      tone: "smartCasual",
      name: "My engagement plan",
    },
  );

  const generatePlanMutation = useMutation({
    mutationKey: ["calendars", calendarId, "calendar", "plans"],
    mutationFn: asMutation(api.calendars.generatePlan),
    onSuccess: () => {
      api.invalidateQueries(api.calendars.getCalendar, [calendarId]);
      onClose();
      setPlanConfiguration({
        from: defaultDates.from,
        to: defaultDates.to,
        channels: [],
        tone: "smartCasual",
        name: "My engagement plan",
      });
    },
  });

  const canManageAnnouncements =
    util.hasPermission(user, "challenge.manageAnnouncements", challengeId) ||
    (user
      ? util.hasPermission(user, "org.manageChallenges", user.organisation)
      : false);

  const buildPlanRequestBody = () => ({
    from: new Date(planConfiguration.from).toISOString(),
    to: new Date(planConfiguration.to).toISOString(),
    name: planConfiguration.name,
    tone: planConfiguration.tone,
    channels: planConfiguration.channels.map(
      ({ channel, frequency, count }) => ({ channel, frequency, count }),
    ),
  });

  const getChannel = (channelName: string) =>
    planConfiguration.channels.find((c) => c.channel === channelName);

  return (
    <Modal open={generatePlanModalOpen} onClose={onClose} size="small">
      <ModalHeader>Generate a new engagement plan</ModalHeader>
      <ModalContent scrolling>
        <H5>Step 1: Configure your plan</H5>
        <FormField>
          <FormLabel>Name your plan</FormLabel>
          <Input
            fluid
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setPlanConfiguration((prev) => ({
                ...prev,
                name: e.target.value,
              }))
            }
            placeholder="My engagement plan"
            value={planConfiguration.name}
          />
        </FormField>
        <H5>
          I want to generate engagement activity to take place between these
          dates
        </H5>
        <Grid columns={2} mobile={1}>
          <GridColumn>
            <FormField>
              <FormLabel>Start:</FormLabel>
              <DateInput
                key="from"
                dateFormat="yyyy-MM-dd"
                value={planConfiguration.from}
                onChange={(_e, { value }) =>
                  setPlanConfiguration((prev) => ({
                    ...prev,
                    from: value ?? "",
                  }))
                }
              />
            </FormField>
          </GridColumn>
          <GridColumn>
            <FormField>
              <FormLabel>End:</FormLabel>
              <DateInput
                key="end"
                dateFormat="yyyy-MM-dd"
                value={planConfiguration.to}
                onChange={(_e, { value }) =>
                  setPlanConfiguration((prev) => ({ ...prev, to: value ?? "" }))
                }
              />
            </FormField>
          </GridColumn>
        </Grid>
        <H5>Choose which engagement channels to use</H5>
        <Checkbox
          style={{ display: "block", marginBottom: 10 }}
          checked={!!getChannel("email")}
          onChange={(_e, { checked }) =>
            setPlanConfiguration((prev) => ({
              ...prev,
              channels: checked
                ? [
                    ...prev.channels,
                    { channel: "email", frequency: "monthly", count: 1 },
                  ]
                : prev.channels.filter((c) => c.channel !== "email"),
            }))
          }
        >
          Direct email
        </Checkbox>
        {getChannel("email") && (
          <div style={{ display: "flex", alignItems: "center", margin: 15 }}>
            Schedule
            <Input
              size="small"
              value={getChannel("email")?.count}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setPlanConfiguration((prev) => ({
                  ...prev,
                  channels: prev.channels.map((c) =>
                    c.channel === "email"
                      ? { ...c, count: Number(e.target.value) }
                      : c,
                  ),
                }))
              }
              type="number"
              style={{ maxWidth: 70, marginRight: 8, marginLeft: 8 }}
            />
            per
            <Dropdown
              size="small"
              variant="outline"
              value={getChannel("email")?.frequency || ""}
              style={{ marginRight: 8, marginLeft: 8 }}
              options={[
                { value: "weekly", text: "Week" },
                { value: "fortnightly", text: "Fortnight" },
                { value: "monthly", text: "Month" },
              ]}
              onChange={(_e, { value }) =>
                setPlanConfiguration((prev) => ({
                  ...prev,
                  channels: prev.channels.map((c) =>
                    c.channel === "email"
                      ? { ...c, frequency: String(value) }
                      : c,
                  ),
                }))
              }
            />
          </div>
        )}
        {canManageAnnouncements && (
          <Checkbox
            style={{ display: "block", marginBottom: 10 }}
            checked={!!getChannel("announcement")}
            onChange={(_e, { checked }) =>
              setPlanConfiguration((prev) => ({
                ...prev,
                channels: checked
                  ? [
                      ...prev.channels,
                      {
                        channel: "announcement",
                        frequency: "monthly",
                        count: 1,
                      },
                    ]
                  : prev.channels.filter((c) => c.channel !== "announcement"),
              }))
            }
          >
            {`${util.appName()} announcement to audience`}
          </Checkbox>
        )}
        {getChannel("announcement") && (
          <div style={{ display: "flex", alignItems: "center", margin: 15 }}>
            Schedule
            <Input
              value={getChannel("announcement")?.count}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setPlanConfiguration((prev) => ({
                  ...prev,
                  channels: prev.channels.map((c) =>
                    c.channel === "announcement"
                      ? { ...c, count: Number(e.target.value) }
                      : c,
                  ),
                }))
              }
              size="small"
              type="number"
              style={{ maxWidth: 70, marginRight: 8, marginLeft: 8 }}
            />
            per
            <Dropdown
              variant="outline"
              size="small"
              value={getChannel("announcement")?.frequency || ""}
              style={{ marginRight: 8, marginLeft: 8 }}
              options={[
                { value: "weekly", text: "Week" },
                { value: "fortnightly", text: "Fortnight" },
                { value: "monthly", text: "Month" },
              ]}
              onChange={(_e, data: DropdownProps) =>
                setPlanConfiguration((prev) => ({
                  ...prev,
                  channels: prev.channels.map((c) =>
                    c.channel === "announcement"
                      ? { ...c, frequency: String(data.value) }
                      : c,
                  ),
                }))
              }
            />
          </div>
        )}
        <Checkbox
          style={{ display: "block", marginBottom: 10 }}
          checked={!!getChannel("teams")}
          onChange={(_e, { checked }) =>
            setPlanConfiguration((prev) => ({
              ...prev,
              channels: checked
                ? [
                    ...prev.channels,
                    { channel: "teams", frequency: "monthly", count: 1 },
                  ]
                : prev.channels.filter((c) => c.channel !== "teams"),
            }))
          }
        >
          Teams message
        </Checkbox>
        {getChannel("teams") && (
          <div style={{ display: "flex", alignItems: "center", margin: 15 }}>
            Schedule
            <Input
              value={getChannel("teams")?.count}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setPlanConfiguration((prev) => ({
                  ...prev,
                  channels: prev.channels.map((c) =>
                    c.channel === "teams"
                      ? { ...c, count: Number(e.target.value) }
                      : c,
                  ),
                }))
              }
              size="small"
              type="number"
              style={{ maxWidth: 70, marginRight: 8, marginLeft: 8 }}
            />
            per
            <Dropdown
              variant="outline"
              value={getChannel("teams")?.frequency || ""}
              size="small"
              style={{ marginRight: 8, marginLeft: 8 }}
              options={[
                { value: "weekly", text: "Week" },
                { value: "fortnightly", text: "Fortnight" },
                { value: "monthly", text: "Month" },
              ]}
              onChange={(_e, data: DropdownProps) =>
                setPlanConfiguration((prev) => ({
                  ...prev,
                  channels: prev.channels.map((c) =>
                    c.channel === "teams"
                      ? { ...c, frequency: String(data.value) }
                      : c,
                  ),
                }))
              }
            />
          </div>
        )}
        <Checkbox
          style={{ display: "block", marginBottom: 10 }}
          checked={!!getChannel("social")}
          onChange={(_e, { checked }) =>
            setPlanConfiguration((prev) => ({
              ...prev,
              channels: checked
                ? [
                    ...prev.channels,
                    { channel: "social", frequency: "monthly", count: 1 },
                  ]
                : prev.channels.filter((c) => c.channel !== "social"),
            }))
          }
        >
          Social post
        </Checkbox>
        {getChannel("social") && (
          <div style={{ display: "flex", alignItems: "center", margin: 15 }}>
            Schedule
            <Input
              value={getChannel("social")?.count}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setPlanConfiguration((prev) => ({
                  ...prev,
                  channels: prev.channels.map((c) =>
                    c.channel === "social"
                      ? { ...c, count: Number(e.target.value) }
                      : c,
                  ),
                }))
              }
              size="small"
              type="number"
              style={{ maxWidth: 70, marginRight: 8, marginLeft: 8 }}
            />
            per
            <Dropdown
              variant="outline"
              value={getChannel("social")?.frequency || ""}
              size="small"
              style={{ marginRight: 8, marginLeft: 8 }}
              options={[
                { value: "weekly", text: "Week" },
                { value: "fortnightly", text: "Fortnight" },
                { value: "monthly", text: "Month" },
              ]}
              onChange={(_e, data: DropdownProps) =>
                setPlanConfiguration((prev) => ({
                  ...prev,
                  channels: prev.channels.map((c) =>
                    c.channel === "social"
                      ? { ...c, frequency: String(data.value) }
                      : c,
                  ),
                }))
              }
            />
          </div>
        )}
        <H5>Choose a tone-of-voice</H5>
        <Checkbox
          style={{ display: "block", marginBottom: 10 }}
          type="radio"
          name="tone"
          value="smartCasual"
          checked={planConfiguration.tone === "smartCasual"}
          onChange={(_e, { value }) =>
            setPlanConfiguration((prev) => ({
              ...prev,
              tone: value as PlanConfiguration["tone"],
            }))
          }
        >
          Smart-casual
        </Checkbox>
        <Checkbox
          style={{ display: "block", marginBottom: 10 }}
          type="radio"
          name="tone"
          value="formal"
          checked={planConfiguration.tone === "formal"}
          onChange={(_e, { value }) =>
            setPlanConfiguration((prev) => ({
              ...prev,
              tone: value as PlanConfiguration["tone"],
            }))
          }
        >
          Formal
        </Checkbox>
        <Checkbox
          style={{ display: "block", marginBottom: 10 }}
          type="radio"
          name="tone"
          value="auto"
          checked={planConfiguration.tone === "auto"}
          onChange={(_e, { value }) =>
            setPlanConfiguration((prev) => ({
              ...prev,
              tone: value as PlanConfiguration["tone"],
            }))
          }
        >
          Auto-detect
        </Checkbox>
        <Divider hidden />
        <Message style={{ background: "#f7f7f7", color: "#555" }}>
          You&apos;ll be able to update your engagement plan at any time.
        </Message>
        <AiWarning />
      </ModalContent>

      <ModalActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          tooltip={
            (!planConfiguration.name || !planConfiguration.channels.length) &&
            "Please ensure you have filled out the form before continuing."
          }
          onClick={() =>
            generatePlanMutation.mutate([
              calendarId,
              buildPlanRequestBody() as never as OpenAPI.POST<"/calendars/{calendar_id}/calendar/plans">["requestBody"],
            ])
          }
          loading={generatePlanMutation.isPending}
          disabled={
            generatePlanMutation.isPending ||
            !planConfiguration.name ||
            !planConfiguration.channels.length
          }
          color="primary"
        >
          Finish
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default GeneratePlanModal;
